import { LocationHierarchyEntity } from '@smatchy/sdk';
import { buildLocationHierarchyFromAddressComponents } from '@smatchy/location';
import { getSharedGoogleGeocoder } from './place-geocode-cache.util';

// The pure part (address components to hierarchy) lives in @smatchy/location,
// shared with the pro web space. Re-exported so the screens import from here as before.
export {
  isActivityHierarchyComplete,
  mergeLocationHierarchies,
  needsHierarchyEnrichment,
  buildLocationHierarchyFromAddressComponents,
} from '@smatchy/location';

/**
 * Reverse-geocode lat/lng to fill missing region (admin_level_1).
 * Prefer results that carry admin1 so we get Île-de-France, not a POI.
 */
const HIERARCHY_CACHE_TTL_MS = 10 * 60 * 1000;
const HIERARCHY_CACHE_LIMIT = 100;
const HIERARCHY_REQUEST_TIMEOUT_MS = 8000;
const hierarchyEnrichmentCache = new Map<
  string,
  {
    expiresAt: number;
    promise: Promise<LocationHierarchyEntity | undefined>;
  }
>();

export function enrichHierarchyFromLatLng(
  lat: number,
  lng: number,
  language: string,
  selectedPlaceId?: string,
  selectedLevel?: string,
): Promise<LocationHierarchyEntity | undefined> {
  const key = [lat.toFixed(5), lng.toFixed(5), language, selectedPlaceId || '', selectedLevel || ''].join(':');
  const now = Date.now();
  const existing = hierarchyEnrichmentCache.get(key);
  if (existing && existing.expiresAt > now) {
    return existing.promise;
  }
  if (existing) {
    hierarchyEnrichmentCache.delete(key);
  }
  if (hierarchyEnrichmentCache.size >= HIERARCHY_CACHE_LIMIT) {
    const oldestKey = hierarchyEnrichmentCache.keys().next().value as string | undefined;
    if (oldestKey) {
      hierarchyEnrichmentCache.delete(oldestKey);
    }
  }

  const promise = new Promise<LocationHierarchyEntity | undefined>((resolve) => {
    let settled = false;
    const geocoder = getSharedGoogleGeocoder();
    /*
     * The Maps SDK can be absent -- a blocked script, a device with no network
     * at startup -- and the shared geocoder answers undefined then, which the
     * city search already survives. This one called geocode on it regardless.
     * No geocoder means no hierarchy to enrich with, which is what nothing
     * says here; the cache drops the entry, so the next call tries again once
     * the SDK has arrived.
     */
    if (!geocoder) {
      resolve(undefined);
      return;
    }
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(undefined);
      }
    }, HIERARCHY_REQUEST_TIMEOUT_MS);
    geocoder.geocode(
      {
        location: { lat, lng },
        language,
      },
      (results, status) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeout);
        if (status !== 'OK' || !results?.length) {
          resolve(undefined);
          return;
        }
        resolve(pickHierarchyFromGeocodeResults(results, selectedPlaceId, selectedLevel));
      },
    );
  });
  hierarchyEnrichmentCache.set(key, {
    expiresAt: now + HIERARCHY_CACHE_TTL_MS,
    promise,
  });
  void promise.then((result) => {
    if (!result && hierarchyEnrichmentCache.get(key)?.promise === promise) {
      hierarchyEnrichmentCache.delete(key);
    }
  });
  return promise;
}

function pickHierarchyFromGeocodeResults(
  results: google.maps.GeocoderResult[],
  selectedPlaceId?: string,
  selectedLevel?: string,
): LocationHierarchyEntity | undefined {
  let best: LocationHierarchyEntity | undefined;
  for (const result of results) {
    const built = buildLocationHierarchyFromAddressComponents(
      result.address_components || [],
      selectedPlaceId,
      selectedLevel,
    );
    if (built.region?.name) {
      return built;
    }
    if (!best) {
      best = built;
    }
  }
  return best;
}
